import React, { useRef, useState, useMemo, useCallback, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import {
    Button,
    StarRatingPicker,
    HeadingText50,
    BodyTextMedium,
} from '@modernhealth/vital-components';

import { ReviewConfig, ReviewState, SubmissionState } from './ReviewConfig';
import {
    Question,
    QuestionInput,
    QUESTION_LABEL_ID,
    QuestionDisplayField,
    DEFAULT_DISPLAY_ORDER,
} from './ReviewInputs';
import { getTextAsString } from './ReviewHelpers';

export type { MultiSelect, NumbersRow, Slider, Textarea } from './ReviewInputs';
export { QuestionFormat, QuestionDisplayField } from './ReviewInputs';
export type { ReviewConfig, ReviewState } from './ReviewConfig';
export { getValueFromReviewState } from './ReviewConfig';

interface ReviewProps<K extends string = string> {
    config: ReviewConfig<K>;
    state?: ReviewState<K>;
    onNext: (state: ReviewState<K>) => Promise<void> | void;
    onSubmit: (state: ReviewState<K>) => Promise<void> | void;
    onRender?: (uiState: ReviewUiState, question?: Question<K>) => Promise<void> | void;
    renderHeader?: (uiState: ReviewUiState, question?: Question<K>) => JSX.Element | null;
}

function useReviewState<K extends string>(state: ReviewState<K> = {}) {
    const [localState, setLocalState] = useState(state);

    const setRating = useCallback(
        (d: number) => setLocalState(s => ({ ...s, rating: d })),
        [setLocalState],
    );

    const setAnswer = useCallback(
        (key: K, value: unknown) =>
            setLocalState(s => ({
                ...s,
                questions: {
                    ...(s.questions || {}),
                    [key]: value,
                },
            })),
        [setLocalState],
    );

    const clearAnswers = useCallback(
        () =>
            setLocalState(s => ({
                ...s,
                questions: {},
                currentQuestionIndex: 0,
            })),
        [setLocalState],
    );

    const setCurrentQuestionIndex = useCallback(
        (currentQuestionIndex: number) =>
            setLocalState(s => ({
                ...s,
                currentQuestionIndex,
            })),
        [setLocalState],
    );

    const setSubmission = useCallback(
        (submission: SubmissionState) =>
            setLocalState(s => ({
                ...s,
                submission,
            })),
        [setLocalState],
    );

    return useMemo(
        () =>
            [
                localState,
                {
                    setRating,
                    setAnswer,
                    clearAnswers,
                    setCurrentQuestionIndex,
                    setSubmission,
                    setReviewState: setLocalState,
                },
            ] as const,
        [localState, setRating, setAnswer, clearAnswers, setCurrentQuestionIndex, setSubmission],
    );
}

export enum ReviewUiState {
    RATING,
    QUESTION_WITH_RATING,
    QUESTION,
    NONE,
}

const EMPTY_QUESTIONS: Question[] = [];

function getReviewUiState<K>({
    config,
    rating,
    currentQuestionIndex,
}: {
    config: ReviewConfig<K>;
    rating: number | null | undefined;
    currentQuestionIndex: number;
}) {
    const questionTree =
        rating !== undefined && rating !== null
            ? config.rating.questionTree.find(([maxRating]) => maxRating >= rating)
            : undefined;
    const ratingQuestionList = questionTree ? questionTree[1] : [];
    const questions = config.questions ?? (EMPTY_QUESTIONS as unknown as Question<K>[]);
    const nextQuestionIndex =
        currentQuestionIndex + 1 < ratingQuestionList.length + questions.length
            ? currentQuestionIndex + 1
            : undefined;

    if (rating === undefined) {
        return {
            state: ReviewUiState.RATING,
            nextQuestionIndex,
            questionTree,
        } as const;
    }

    if (currentQuestionIndex < ratingQuestionList.length) {
        return {
            state: ReviewUiState.QUESTION_WITH_RATING,
            currentQuestion: ratingQuestionList[currentQuestionIndex],
            nextQuestionIndex,
            questionTree,
        } as const;
    }

    const questionsIndex = currentQuestionIndex - ratingQuestionList.length;
    if (questionsIndex < questions.length) {
        return {
            state: ReviewUiState.QUESTION,
            currentQuestion: questions[questionsIndex],
            nextQuestionIndex,
            questionTree,
        } as const;
    }

    return {
        state: ReviewUiState.NONE,
    } as const;
}

function QuestionField<K extends string = string>({
    field,
    renderHeader,
    question,
    reviewUiState,
}: {
    field: QuestionDisplayField;
    renderHeader: ReviewProps<K>['renderHeader'];
    question: Question<K>;
    reviewUiState: ReviewUiState;
}) {
    const { t } = useTranslation();
    const { title, subtitle } = question;

    if (field === QuestionDisplayField.HEADER) {
        return renderHeader?.(reviewUiState, question) ?? null;
    }

    if (field === QuestionDisplayField.TITLE) {
        return (
            <HeadingText50 fontColor="gray85" marginBottom={10} centered>
                <label id={QUESTION_LABEL_ID}>{getTextAsString(t, title)}</label>
            </HeadingText50>
        );
    }

    if (field === QuestionDisplayField.SUBTITLE && subtitle) {
        return (
            <BodyTextMedium fontColor="gray65" marginBottom={10} centered>
                {getTextAsString(t, subtitle)}
            </BodyTextMedium>
        );
    }

    return null;
}

export function Review<K extends string = string>({
    config,
    state,
    onNext,
    onSubmit,
    onRender,
    renderHeader,
}: ReviewProps<K>) {
    const { t } = useTranslation();
    const [reviewState, { setRating, setAnswer, clearAnswers, setSubmission, setReviewState }] =
        useReviewState(state);
    const {
        rating,
        questions,
        currentQuestionIndex = 0,
        submission = SubmissionState.NONE,
    } = reviewState;

    // Keep review state in sync if the parent updates it.
    useEffect(() => {
        if (state) {
            setReviewState(state);
        }
    }, [state, setReviewState]);

    const reviewUiState = getReviewUiState({
        config,
        rating,
        currentQuestionIndex,
    });

    const setCurrentQuestionAnswer = useCallback(
        (v: unknown) => {
            if (!reviewUiState.currentQuestion) {
                return;
            }

            setAnswer(reviewUiState.currentQuestion.key, v);
        },
        [setAnswer, reviewUiState.currentQuestion],
    );

    useEffect(() => {
        onRender?.(reviewUiState.state, reviewUiState.currentQuestion);
        // We do not want to call this again if the onRender function changes
        // so we are excluding it from the deps list explicitly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reviewUiState.state, reviewUiState.currentQuestion]);

    // We do not want this to run on mount as it will clear instead of comparing
    // the previous questionTree with the current questionTree.
    const mounted = useRef(false);
    useLayoutEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            return;
        }

        if (reviewUiState.questionTree) {
            clearAnswers();
        }
    }, [reviewUiState.questionTree, clearAnswers]);

    // Clean up the mounted ref to ensure we don't setState after this
    // component has unmounted.
    useEffect(
        () => () => {
            mounted.current = false;
        },
        [],
    );

    const submit = rating !== undefined && !reviewUiState.nextQuestionIndex;
    const actionButton = submit ? (
        <Button
            size="large"
            fluid
            css={{
                marginTop: 30,
            }}
            onClick={async () => {
                try {
                    setSubmission(SubmissionState.SUBMITTING);
                    await onSubmit(reviewState);
                    if (mounted.current) {
                        setSubmission(SubmissionState.SUBMITTED);
                    }
                } catch (e) {
                    setSubmission(SubmissionState.NONE);
                }
            }}
            busy={submission === SubmissionState.SUBMITTING}
            disabled={submission === SubmissionState.SUBMITTED}
        >
            {submission === SubmissionState.SUBMITTED
                ? t('shared:review.action.submitted')
                : t('shared:review.action.submit')}
        </Button>
    ) : (
        <Button
            size="large"
            fluid
            css={{
                marginTop: 30,
            }}
            onClick={() =>
                onNext({
                    ...reviewState,
                    currentQuestionIndex: reviewUiState.nextQuestionIndex,
                })
            }
        >
            {t('shared:review.action.continue')}
        </Button>
    );

    if (reviewUiState.state === ReviewUiState.RATING) {
        return (
            <ContentContainer>
                {renderHeader?.(ReviewUiState.RATING)}
                <HeadingText50 fontColor="gray85" centered marginBottom={16}>
                    {getTextAsString(t, config.rating.title)}
                </HeadingText50>
                <BodyTextMedium fontColor="gray65" centered marginBottom={40}>
                    {getTextAsString(t, config.rating.subtitle)}
                </BodyTextMedium>
                <StarRatingPicker
                    defaultRating={rating ?? undefined}
                    onRatingChange={d =>
                        onNext({
                            ...reviewState,
                            rating: d,
                        })
                    }
                />
                <Button
                    size="large"
                    fluid
                    variant="flat"
                    css={{
                        marginTop: 30,
                    }}
                    onClick={() =>
                        onNext({
                            ...reviewState,
                            rating: null,
                        })
                    }
                >
                    {t('shared:review.action.skip')}
                </Button>
            </ContentContainer>
        );
    }

    if (reviewUiState.state === ReviewUiState.QUESTION_WITH_RATING) {
        const { currentQuestion } = reviewUiState;
        const { displayOrder = DEFAULT_DISPLAY_ORDER } = currentQuestion;

        return (
            <ContentContainer>
                {displayOrder.map(field => (
                    <QuestionField
                        key={field}
                        field={field}
                        renderHeader={renderHeader}
                        question={currentQuestion}
                        reviewUiState={ReviewUiState.QUESTION_WITH_RATING}
                    />
                ))}
                <div css={{ marginBottom: 40 }}>
                    <StarRatingPicker
                        defaultRating={rating ?? undefined}
                        onRatingChange={d => setRating(d)}
                    />
                </div>
                <QuestionInput
                    key={currentQuestion.key}
                    question={currentQuestion}
                    state={questions?.[currentQuestion.key]}
                    onStateChange={setCurrentQuestionAnswer}
                />
                {actionButton}
            </ContentContainer>
        );
    }

    if (reviewUiState.state === ReviewUiState.QUESTION) {
        const { currentQuestion, nextQuestionIndex } = reviewUiState;
        const { displayOrder = DEFAULT_DISPLAY_ORDER } = currentQuestion;

        return (
            <ContentContainer>
                {displayOrder.map(field => (
                    <QuestionField
                        key={field}
                        field={field}
                        renderHeader={renderHeader}
                        question={currentQuestion}
                        reviewUiState={ReviewUiState.QUESTION_WITH_RATING}
                    />
                ))}
                <Spacer height={40} />
                <QuestionInput
                    key={currentQuestion.key}
                    question={currentQuestion}
                    state={questions?.[currentQuestion.key]}
                    onStateChange={setCurrentQuestionAnswer}
                />
                {submit || questions?.[currentQuestion.key] ? (
                    actionButton
                ) : (
                    <Button
                        size="large"
                        fluid
                        variant="flat"
                        css={{
                            marginTop: 30,
                        }}
                        onClick={() =>
                            onNext({ ...reviewState, currentQuestionIndex: nextQuestionIndex })
                        }
                    >
                        {t('shared:review.action.skip')}
                    </Button>
                )}
            </ContentContainer>
        );
    }

    return null;
}

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Spacer = styled.div<{ height: number }>`
    margin-bottom: ${({ height }) => height}px;
`;
